/**
 * AssessmentConfigurationModal Component
 * 
 * Modal for configuring assessment settings at topic or subject level.
 * Supports configuration inheritance from subject level and AI generation rules.
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Grid,
    Divider,
    Paper,
    Chip,
    MenuItem
} from '@mui/material';
import { Info as InfoIcon, Warning as WarningIcon, AutoFixHigh as AIIcon } from '@mui/icons-material';
import {
    AssessmentConfigDTO,
    ResolvedAssessmentConfigDTO,
    getAssessmentConfig,
    saveAssessmentConfig,
    resolveAssessmentConfig
} from '../../services/adminAssessmentService';

interface AssessmentConfigurationModalProps {
    open: boolean;
    onClose: () => void;
    level: 'SUBJECT' | 'TOPIC';
    entityId: number;
    entityName?: string;
    subjectId?: number; // For topic-level config to resolve inheritance
}

/**
 * AssessmentConfigurationModal - Configure assessment settings
 * 
 * Features:
 * - View current effective configuration
 * - Override inherited settings
 * - Configure AI generation rules
 * - Set reassessment policies
 */
const AssessmentConfigurationModal: React.FC<AssessmentConfigurationModalProps> = ({
    open,
    onClose,
    level,
    entityId,
    entityName,
    subjectId
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Configuration states
    const [localConfig, setLocalConfig] = useState<AssessmentConfigDTO | null>(null);
    const [effectiveConfig, setEffectiveConfig] = useState<ResolvedAssessmentConfigDTO | null>(null);

    // Form State
    const [formData, setFormData] = useState<AssessmentConfigDTO>({
        level,
        entityId,
        maxQuestions: 20,
        timeLimitMinutes: 60,
        minPassPercent: 70,
        maxAttempts: 1,
        requiresWorkSubmission: false,
        allowReassessment: true,
        reassessmentGateDays: 7,
        allowAiGeneration: true,
        aiDifficultyLevel: 'MEDIUM',
        aiQuestionTypes: ['MULTIPLE_CHOICE', 'SHORT_ANSWER']
    });

    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open, level, entityId]);

    /**
     * Load configuration data
     */
    const loadData = async () => {
        setLoading(true);
        setError(null);
        setEditMode(false);
        try {
            // Try to get local config first
            const localRes = await getAssessmentConfig(level, entityId);
            const isLocalConfigPresent = localRes.maxQuestions != null;

            if (isLocalConfigPresent) {
                setLocalConfig(localRes);
                setFormData(localRes);
                setEffectiveConfig({
                    ...localRes,
                    source: level,
                    sourceId: entityId
                } as ResolvedAssessmentConfigDTO);
            } else {
                setLocalConfig(null);
                // Get resolved/inherited config
                const topicIdParam = level === 'TOPIC' ? entityId : undefined;
                const subjectIdParam = level === 'SUBJECT' ? entityId : subjectId;
                
                const resolved = await resolveAssessmentConfig(topicIdParam, subjectIdParam);
                setEffectiveConfig(resolved);
                // Pre-fill form with effective values
                setFormData({
                    level,
                    entityId,
                    maxQuestions: resolved.maxQuestions,
                    timeLimitMinutes: resolved.timeLimitMinutes,
                    minPassPercent: resolved.minPassPercent,
                    maxAttempts: resolved.maxAttempts,
                    requiresWorkSubmission: resolved.requiresWorkSubmission,
                    allowReassessment: resolved.allowReassessment,
                    reassessmentGateDays: resolved.reassessmentGateDays,
                    allowAiGeneration: resolved.allowAiGeneration,
                    aiDifficultyLevel: resolved.aiDifficultyLevel,
                    aiQuestionTypes: resolved.aiQuestionTypes
                });
            }
        } catch (err: any) {
            console.error('Failed to load config', err);
            // Use defaults if API fails
            setEffectiveConfig({
                source: 'SYSTEM',
                sourceId: 0,
                level: 'SYSTEM',
                entityId: 0,
                maxQuestions: 20,
                timeLimitMinutes: 60,
                minPassPercent: 70,
                maxAttempts: 1,
                requiresWorkSubmission: false,
                allowReassessment: true,
                reassessmentGateDays: 7,
                allowAiGeneration: true,
                aiDifficultyLevel: 'MEDIUM',
                aiQuestionTypes: ['MULTIPLE_CHOICE', 'SHORT_ANSWER']
            });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle save
     */
    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            await saveAssessmentConfig(level, entityId, formData);
            setSuccessMsg('Configuration saved successfully.');
            setTimeout(() => {
                onClose();
                setSuccessMsg(null);
            }, 1500);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save configuration.');
            setLoading(false);
        }
    };

    /**
     * Handle question type toggle
     */
    const handleQuestionTypeToggle = (type: string) => {
        const currentTypes = formData.aiQuestionTypes || [];
        if (currentTypes.includes(type)) {
            setFormData(prev => ({
                ...prev,
                aiQuestionTypes: currentTypes.filter(t => t !== type)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                aiQuestionTypes: [...currentTypes, type]
            }));
        }
    };

    if (loading && !effectiveConfig) {
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Assessment Configuration: {entityName || level}
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}
                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>
                )}

                {/* View Mode: Show Effective Config Summary */}
                {!editMode && effectiveConfig && (
                    <Box>
                        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                            {localConfig ? (
                                <Typography variant="body2">
                                    This {level.toLowerCase()} has customized settings that override inherited values.
                                </Typography>
                            ) : (
                                <Typography variant="body2">
                                    Using settings inherited from <strong>{effectiveConfig.source}</strong>.
                                    Click "Override Configuration" to customize.
                                </Typography>
                            )}
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Assessment Settings</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">Max Questions</Typography>
                                    <Typography variant="h6">{effectiveConfig.maxQuestions}</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">Time Limit</Typography>
                                    <Typography variant="h6">{effectiveConfig.timeLimitMinutes} min</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">Pass Score</Typography>
                                    <Typography variant="h6">{effectiveConfig.minPassPercent}%</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">Max Attempts</Typography>
                                    <Typography variant="h6">{effectiveConfig.maxAttempts}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Reassessment Policy</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="textSecondary">Work Submission</Typography>
                                    <Typography variant="body1">
                                        {effectiveConfig.requiresWorkSubmission ? 'Required' : 'Not Required'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="textSecondary">Reassessment</Typography>
                                    <Typography variant="body1">
                                        {effectiveConfig.allowReassessment ? 'Allowed' : 'Not Allowed'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="textSecondary">Gate Period</Typography>
                                    <Typography variant="body1">
                                        {effectiveConfig.reassessmentGateDays} days
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                <AIIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                                AI Generation Settings
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="textSecondary">AI Generation</Typography>
                                    <Typography variant="body1">
                                        {effectiveConfig.allowAiGeneration ? 'Enabled' : 'Disabled'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="caption" color="textSecondary">Difficulty Level</Typography>
                                    <Typography variant="body1">{effectiveConfig.aiDifficultyLevel}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="textSecondary">Question Types</Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                        {effectiveConfig.aiQuestionTypes?.map(type => (
                                            <Chip key={type} label={type.replace('_', ' ')} size="small" />
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                )}

                {/* Edit Mode Form */}
                {editMode && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Assessment Settings</Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Max Questions"
                                    type="number"
                                    fullWidth
                                    value={formData.maxQuestions || ''}
                                    onChange={(e) => setFormData({ ...formData, maxQuestions: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Time Limit (Minutes)"
                                    type="number"
                                    fullWidth
                                    value={formData.timeLimitMinutes || ''}
                                    onChange={(e) => setFormData({ ...formData, timeLimitMinutes: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Min Pass %"
                                    type="number"
                                    fullWidth
                                    value={formData.minPassPercent || ''}
                                    onChange={(e) => setFormData({ ...formData, minPassPercent: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Max Attempts"
                                    type="number"
                                    fullWidth
                                    value={formData.maxAttempts || ''}
                                    onChange={(e) => setFormData({ ...formData, maxAttempts: parseInt(e.target.value) })}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>Reassessment Policy</Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.requiresWorkSubmission || false}
                                            onChange={(e) => setFormData({ ...formData, requiresWorkSubmission: e.target.checked })}
                                        />
                                    }
                                    label="Require Work Submission"
                                />
                                <Typography variant="caption" display="block" color="textSecondary">
                                    Students must upload work files
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.allowReassessment || false}
                                            onChange={(e) => setFormData({ ...formData, allowReassessment: e.target.checked })}
                                        />
                                    }
                                    label="Allow Reassessment"
                                />
                                <Typography variant="caption" display="block" color="textSecondary">
                                    Students can retake after failing
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Reassessment Gate (Days)"
                                    type="number"
                                    fullWidth
                                    value={formData.reassessmentGateDays || ''}
                                    onChange={(e) => setFormData({ ...formData, reassessmentGateDays: parseInt(e.target.value) })}
                                    disabled={!formData.allowReassessment}
                                    helperText="Wait period before retake"
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>
                            <AIIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                            AI Generation Rules
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.allowAiGeneration || false}
                                            onChange={(e) => setFormData({ ...formData, allowAiGeneration: e.target.checked })}
                                        />
                                    }
                                    label="Enable AI Question Generation"
                                />
                                <Typography variant="caption" display="block" color="textSecondary">
                                    Allow AI to generate assessment questions for this {level.toLowerCase()}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Default AI Difficulty"
                                    fullWidth
                                    value={formData.aiDifficultyLevel || 'MEDIUM'}
                                    onChange={(e) => setFormData({ ...formData, aiDifficultyLevel: e.target.value })}
                                    disabled={!formData.allowAiGeneration}
                                >
                                    <MenuItem value="EASY">Easy</MenuItem>
                                    <MenuItem value="MEDIUM">Medium</MenuItem>
                                    <MenuItem value="HARD">Hard</MenuItem>
                                    <MenuItem value="ADAPTIVE">Adaptive (Mixed)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Allowed Question Types
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {['MULTIPLE_CHOICE', 'TRUE_FALSE', 'SHORT_ANSWER', 'LONG_ANSWER', 'FILE_UPLOAD'].map(type => (
                                        <Chip
                                            key={type}
                                            label={type.replace('_', ' ')}
                                            onClick={() => formData.allowAiGeneration && handleQuestionTypeToggle(type)}
                                            color={formData.aiQuestionTypes?.includes(type) ? 'primary' : 'default'}
                                            variant={formData.aiQuestionTypes?.includes(type) ? 'filled' : 'outlined'}
                                            disabled={!formData.allowAiGeneration}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                {!editMode ? (
                    <Button onClick={() => setEditMode(true)} variant="contained" color="primary">
                        {localConfig ? 'Edit Configuration' : 'Override Configuration'}
                    </Button>
                ) : (
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AssessmentConfigurationModal;
